'use client';

import React, { useState, useRef, useEffect, useCallback } from 'react';
import Link from 'next/link';
import {
  Bell, CheckCircle, XCircle, X, CheckCheck, AtSign,
  ThumbsUp, TrendingUp, BookOpen, Loader2, ArrowRight,
} from 'lucide-react';
import { useNotifications, AppNotification } from '@/contexts/NotificationContext';
import { MentionNotification } from '@/lib/db/mentions';
import { useAuth } from '@/contexts/AuthContext';
import {
  fetchHubNotifications,
  markHubNotificationRead,
  markAllHubNotificationsRead,
  HubNotification,
  HubNotificationType,
} from '@/lib/db/hub-notifications';
import Icon from '@/components/ui/AppIcon';


// ── Hub notification type config ──────────────────────────────────────────────

const HUB_TYPE_CONFIG: Record<
  HubNotificationType,
  { icon: React.ElementType; color: string; bg: string }
> = {
  contribution_approved: { icon: CheckCircle, color: '#4ade80', bg: 'rgba(74,222,128,0.10)' },
  contribution_rejected: { icon: XCircle, color: '#f87171', bg: 'rgba(248,113,113,0.10)' },
  corroboration_received: { icon: ThumbsUp, color: '#60a5fa', bg: 'rgba(96,165,250,0.10)' },
  rank_changed: { icon: TrendingUp, color: '#f59e0b', bg: 'rgba(245,158,11,0.10)' },
  new_topic: { icon: BookOpen, color: 'var(--primary)', bg: 'rgba(204,0,0,0.08)' },
};

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
}

// ── Notification items ────────────────────────────────────────────────────────

function NotificationItem({
  notif,
  onRead,
}: {
  notif: AppNotification;
  onRead: (id: string) => void;
}) {
  const isApproved = notif.status === 'published';

  return (
    <div
      className="flex gap-3 px-4 py-3 transition-colors duration-150 cursor-pointer"
      style={{
        backgroundColor: notif.readAt ? 'transparent' : 'rgba(224,90,71,0.06)',
        borderBottom: '1px solid var(--border)',
      }}
      onClick={() => onRead(notif.id)}
    >
      <div className="flex-shrink-0 mt-0.5">
        {isApproved ? (
          <CheckCircle size={18} style={{ color: '#4ade80' }} />
        ) : (
          <XCircle size={18} style={{ color: '#f87171' }} />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium leading-snug" style={{ color: 'var(--foreground)' }}>
          {isApproved ? 'Contribution approved' : 'Contribution rejected'}
        </p>
        <p
          className="text-xs mt-0.5 truncate"
          style={{ color: 'var(--muted-foreground)' }}
          title={notif.title}
        >
          {notif.title}
        </p>
        {!isApproved && notif.rejectionReason && (
          <p className="text-xs mt-1 italic" style={{ color: '#f87171' }}>
            {notif.rejectionReason}
          </p>
        )}
        <p className="text-xs mt-1" style={{ color: 'var(--muted-foreground)', opacity: 0.6 }}>
          {new Date(notif.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </p>
      </div>
      {!notif.readAt && (
        <div
          className="flex-shrink-0 w-2 h-2 rounded-full mt-1.5"
          style={{ backgroundColor: 'var(--primary)' }}
        />
      )}
    </div>
  );
}

function MentionNotificationItem({
  notif,
  onRead,
}: {
  notif: MentionNotification;
  onRead: (id: string) => void;
}) {
  const contentLabel =
    notif.contentType === 'comment' ? 'comment'
      : notif.contentType === 'forum_reply' ? 'forum reply' : 'forum post';

  return (
    <div
      className="flex gap-3 px-4 py-3 transition-colors duration-150 cursor-pointer"
      style={{
        backgroundColor: notif.readAt ? 'transparent' : 'rgba(0,128,128,0.06)',
        borderBottom: '1px solid var(--border)',
      }}
      onClick={() => onRead(notif.id)}
    >
      <div className="flex-shrink-0 mt-0.5">
        <AtSign size={18} style={{ color: '#008080' }} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium leading-snug" style={{ color: 'var(--foreground)' }}>
          {notif.actorName ? (
            <>
              <span style={{ color: 'var(--primary)' }}>{notif.actorName}</span>
              {' mentioned you in a '}
              {contentLabel}
            </>
          ) : (
            `You were mentioned in a ${contentLabel}`
          )}
        </p>
        {notif.contentSnippet && (
          <p
            className="text-xs mt-0.5 line-clamp-2 italic"
            style={{ color: 'var(--muted-foreground)' }}
          >
            "{notif.contentSnippet}"
          </p>
        )}
        <p className="text-xs mt-1" style={{ color: 'var(--muted-foreground)', opacity: 0.6 }}>
          {new Date(notif.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </p>
      </div>
      {!notif.readAt && (
        <div
          className="flex-shrink-0 w-2 h-2 rounded-full mt-1.5"
          style={{ backgroundColor: '#008080' }}
        />
      )}
    </div>
  );
}

function HubNotificationItem({
  notif,
  onRead,
}: {
  notif: HubNotification;
  onRead: (id: string) => void;
}) {
  const cfg = HUB_TYPE_CONFIG[notif.type];
  const Icon = cfg.icon;
  const isUnread = !notif.readAt;

  const inner = (
    <div
      className="flex gap-3 px-4 py-3 transition-colors duration-150 cursor-pointer"
      style={{
        backgroundColor: isUnread ? cfg.bg : 'transparent',
        borderBottom: '1px solid var(--border)',
      }}
      onClick={() => isUnread && onRead(notif.id)}
    >
      <div className="flex-shrink-0 mt-0.5">
        <Icon size={18} style={{ color: cfg.color }} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium leading-snug" style={{ color: 'var(--foreground)' }}>
          {notif.title}
        </p>
        {notif.body && (
          <p className="text-xs mt-0.5 line-clamp-2" style={{ color: 'var(--muted-foreground)' }}>
            {notif.body}
          </p>
        )}
        <p className="text-xs mt-1" style={{ color: 'var(--muted-foreground)', opacity: 0.6 }}>
          {timeAgo(notif.createdAt)}
        </p>
      </div>
      {isUnread && (
        <div
          className="flex-shrink-0 w-2 h-2 rounded-full mt-1.5"
          style={{ backgroundColor: cfg.color }}
        />
      )}
    </div>
  );

  if (notif.link) {
    return (
      <Link href={notif.link} className="block">
        {inner}
      </Link>
    );
  }
  return inner;
}

// ── Main component ────────────────────────────────────────────────────────────

type TabType = 'all' | 'mentions' | 'hub';

export default function NotificationBell() {
  const {
    notifications,
    unreadCount,
    markAllRead,
    markRead,
    clearAll,
    mentionNotifications,
    mentionUnreadCount,
    markMentionNotifRead,
    markAllMentionNotifsRead,
    totalUnreadCount,
  } = useNotifications();

  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<TabType>('all');
  const ref = useRef<HTMLDivElement>(null);

  // Hub notifications state
  const [hubNotifications, setHubNotifications] = useState<HubNotification[]>([]);
  const [hubLoading, setHubLoading] = useState(false);
  const [hubLoaded, setHubLoaded] = useState(false);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Load hub notifications when hub tab is first opened
  useEffect(() => {
    if (tab === 'hub' && !hubLoaded && user) {
      setHubLoading(true);
      fetchHubNotifications(user.id).then((data) => {
        setHubNotifications(data);
        setHubLoading(false);
        setHubLoaded(true);
      });
    }
  }, [tab, hubLoaded, user]);

  // Also load hub notifications when popup opens (to get fresh count)
  useEffect(() => {
    if (open && user && !hubLoaded) {
      fetchHubNotifications(user.id).then((data) => {
        setHubNotifications(data);
        setHubLoaded(true);
      });
    }
  }, [open, user, hubLoaded]);

  const hubUnreadCount = hubNotifications.filter((n) => !n.readAt).length;

  const totalBadgeCount = totalUnreadCount + hubUnreadCount;

  const handleMarkAllRead = useCallback(async () => {
    if (tab === 'all') {
      markAllRead();
    } else if (tab === 'mentions') {
      markAllMentionNotifsRead();
    } else if (tab === 'hub' && user) {
      setHubNotifications((prev) =>
        prev.map((n) => (n.readAt ? n : { ...n, readAt: new Date().toISOString() }))
      );
      await markAllHubNotificationsRead(user.id);
    }
  }, [tab, markAllRead, markAllMentionNotifsRead, user]);

  const handleHubRead = useCallback(async (id: string) => {
    setHubNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, readAt: new Date().toISOString() } : n))
    );
    await markHubNotificationRead(id);
  }, []);

  const currentUnread =
    tab === 'all' ? unreadCount : tab === 'mentions' ? mentionUnreadCount : hubUnreadCount;

  const hasAny =
    tab === 'all'
      ? notifications.length > 0
      : tab === 'mentions'
      ? mentionNotifications.length > 0
      : hubNotifications.length > 0;

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="relative p-2 rounded-md transition-colors duration-150"
        style={{ color: 'var(--muted-foreground)' }}
        aria-label={`Notifications${totalBadgeCount > 0 ? ` (${totalBadgeCount} unread)` : ''}`}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLButtonElement).style.color = 'var(--foreground)';
          (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'var(--muted)';
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLButtonElement).style.color = 'var(--muted-foreground)';
          (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent';
        }}
      >
        <Bell size={18} />
        {totalBadgeCount > 0 && (
          <span
            className="absolute top-1 right-1 flex items-center justify-center rounded-full text-[9px] font-bold leading-none"
            style={{
              backgroundColor: 'var(--primary)',
              color: 'var(--primary-foreground)',
              minWidth: '14px',
              height: '14px',
              padding: '0 3px',
            }}
          >
            {totalBadgeCount > 9 ? '9+' : totalBadgeCount}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-80 rounded-lg shadow-xl overflow-hidden z-50"
          style={{
            backgroundColor: 'var(--card)',
            border: '1px solid var(--border)',
            top: '100%',
          }}
        >
          {/* Header */}
          <div
            className="flex items-center justify-between px-4 py-3"
            style={{ borderBottom: '1px solid var(--border)' }}
          >
            <span className="text-sm font-semibold" style={{ color: 'var(--foreground)' }}>
              Notifications
            </span>
            <div className="flex items-center gap-1">
              {currentUnread > 0 && (
                <button
                  onClick={handleMarkAllRead}
                  className="flex items-center gap-1 text-xs px-2 py-1 rounded transition-colors duration-150"
                  style={{ color: 'var(--muted-foreground)' }}
                  title="Mark all as read"
                  onMouseEnter={(e) =>
                    ((e.currentTarget as HTMLButtonElement).style.color = 'var(--foreground)')
                  }
                  onMouseLeave={(e) =>
                    ((e.currentTarget as HTMLButtonElement).style.color = 'var(--muted-foreground)')
                  }
                >
                  <CheckCheck size={13} />
                  Mark all read
                </button>
              )}
              {tab === 'all' && notifications.length > 0 && (
                <button
                  onClick={clearAll}
                  className="p-1 rounded transition-colors duration-150"
                  style={{ color: 'var(--muted-foreground)' }}
                  title="Clear all"
                  onMouseEnter={(e) =>
                    ((e.currentTarget as HTMLButtonElement).style.color = 'var(--foreground)')
                  }
                  onMouseLeave={(e) =>
                    ((e.currentTarget as HTMLButtonElement).style.color = 'var(--muted-foreground)')
                  }
                >
                  <X size={14} />
                </button>
              )}
            </div>
          </div>

          {/* Tabs */}
          <div
            className="flex border-b"
            style={{ borderColor: 'var(--border)' }}
          >
            {/* Updates tab */}
            <button
              onClick={() => setTab('all')}
              className="flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-medium transition-colors"
              style={{
                color: tab === 'all' ? 'var(--primary)' : 'var(--muted-foreground)',
                borderBottom: tab === 'all' ? '2px solid var(--primary)' : '2px solid transparent',
              }}
            >
              Updates
              {unreadCount > 0 && (
                <span
                  className="text-[9px] px-1 rounded-full font-bold"
                  style={{ backgroundColor: 'var(--primary)', color: 'var(--primary-foreground)' }}
                >
                  {unreadCount}
                </span>
              )}
            </button>

            {/* Mentions tab */}
            <button
              onClick={() => setTab('mentions')}
              className="flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-medium transition-colors"
              style={{
                color: tab === 'mentions' ? '#008080' : 'var(--muted-foreground)',
                borderBottom: tab === 'mentions' ? '2px solid #008080' : '2px solid transparent',
              }}
            >
              <AtSign size={12} />
              Mentions
              {mentionUnreadCount > 0 && (
                <span
                  className="text-[9px] px-1 rounded-full font-bold"
                  style={{ backgroundColor: '#008080', color: '#fff' }}
                >
                  {mentionUnreadCount}
                </span>
              )}
            </button>

            {/* Hub tab */}
            <button
              onClick={() => setTab('hub')}
              className="flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-medium transition-colors"
              style={{
                color: tab === 'hub' ? '#f59e0b' : 'var(--muted-foreground)',
                borderBottom: tab === 'hub' ? '2px solid #f59e0b' : '2px solid transparent',
              }}
            >
              <Bell size={12} />
              Hub
              {hubUnreadCount > 0 && (
                <span
                  className="text-[9px] px-1 rounded-full font-bold"
                  style={{ backgroundColor: '#f59e0b', color: '#fff' }}
                >
                  {hubUnreadCount}
                </span>
              )}
            </button>
          </div>

          {/* List */}
          <div className="overflow-y-auto" style={{ maxHeight: '360px' }}>
            {tab === 'hub' && hubLoading ? (
              <div className="flex items-center justify-center py-10">
                <Loader2 size={20} className="animate-spin" style={{ color: 'var(--muted-foreground)' }} />
              </div>
            ) : !hasAny ? (
              <div className="flex flex-col items-center justify-center py-10 gap-2">
                {tab === 'mentions' ? (
                  <AtSign size={28} style={{ color: 'var(--muted-foreground)', opacity: 0.4 }} />
                ) : (
                  <Bell size={28} style={{ color: 'var(--muted-foreground)', opacity: 0.4 }} />
                )}
                <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
                  {tab === 'mentions' ? 'No mentions yet' : 'No notifications yet'}
                </p>
              </div>
            ) : tab === 'all' ? (
              notifications.map((n) => (
                <NotificationItem key={n.id} notif={n} onRead={markRead} />
              ))
            ) : tab === 'mentions' ? (
              mentionNotifications.map((n) => (
                <MentionNotificationItem key={n.id} notif={n} onRead={markMentionNotifRead} />
              ))
            ) : (
              hubNotifications.map((n) => (
                <HubNotificationItem key={n.id} notif={n} onRead={handleHubRead} />
              ))
            )}
          </div>

          {/* Footer: View all hub link */}
          <div
            className="px-4 py-2.5 border-t flex items-center justify-center"
            style={{ borderColor: 'var(--border)' }}
          >
            <Link
              href="/notification-hub"
              onClick={() => setOpen(false)}
              className="flex items-center gap-1.5 text-xs font-medium transition-colors duration-150"
              style={{ color: 'var(--muted-foreground)' }}
              onMouseEnter={(e) =>
                ((e.currentTarget as HTMLAnchorElement).style.color = 'var(--foreground)')
              }
              onMouseLeave={(e) =>
                ((e.currentTarget as HTMLAnchorElement).style.color = 'var(--muted-foreground)')
              }
            >
              View full notification hub
              <ArrowRight size={12} />
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
